import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@wondermaps/ui";

interface DeviceChannelData {
  uniqueVisitors: number;
  devices: { device: string; count: number }[];
  qrChannels: { channel: string; count: number }[];
  trafficSources: { source: string; count: number }[];
}

export function DeviceChannel({ data }: { data: DeviceChannelData }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Device & Channel Breakdown</CardTitle>
        <CardDescription>
          Shows how visitors access the map — device type (mobile vs
          desktop), QR scan channels (which physical placements drive
          scans), and traffic sources (referrers, direct, social).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Unique visitors */}
        <div className="rounded-lg border p-3 text-center">
          <p className="text-2xl font-bold">{data.uniqueVisitors.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Unique Map Visitors</p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {/* Devices */}
          <div>
            <h4 className="text-sm font-medium mb-2">Devices</h4>
            {data.devices.length === 0 ? (
              <p className="text-muted-foreground text-sm">No device data.</p>
            ) : (
              <div className="space-y-2">
                {data.devices.map((d) => {
                  const pct =
                    data.uniqueVisitors > 0
                      ? Math.round((d.count / data.uniqueVisitors) * 100)
                      : 0;
                  return (
                    <div key={d.device}>
                      <div className="flex justify-between text-sm">
                        <span className="capitalize">{d.device || "unknown"}</span>
                        <span className="text-muted-foreground">
                          {d.count.toLocaleString()} ({pct}%)
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden mt-1">
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* QR channels */}
          <div>
            <h4 className="text-sm font-medium mb-2">QR Channels</h4>
            {data.qrChannels.length === 0 ? (
              <p className="text-muted-foreground text-sm">No QR scan data.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Channel</TableHead>
                    <TableHead className="text-right">Scans</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.qrChannels.map((ch) => (
                    <TableRow key={ch.channel}>
                      <TableCell>{ch.channel}</TableCell>
                      <TableCell className="text-right">
                        {ch.count.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Traffic sources */}
          <div>
            <h4 className="text-sm font-medium mb-2">Traffic Sources</h4>
            {data.trafficSources.length === 0 ? (
              <p className="text-muted-foreground text-sm">No referrer data.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.trafficSources.map((s) => (
                    <TableRow key={s.source}>
                      <TableCell>{s.source}</TableCell>
                      <TableCell className="text-right">
                        {s.count.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
